import { usePriceList } from "../../../../providers/PriceListProvider";
import { usePictureSets } from "../../../../hooks/usePictureSets";
import { useShoppingCart } from "../../../../providers/ShoppingCartProvider";
import useRoutes from "../../../../hooks/useRoutes";
import { Trans, useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import Container from "@mui/material/Container";
import { generatePath } from "react-router";
import MUILink from "@mui/material/Link";
import { clientIsDesktop, formatCurrency } from "../../../../../utils";
import TemplateList, {
  TemplateListItemsSkeleton,
} from "../../../../Components/Template/TemplateList";
import ProductPriceInfoPopup from "../../../Components/ProductPriceInfoPopup";
import { Skeleton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import useViewItemList from "../../../../hooks/useViewItemList";
import {
  templateList,
  templateProductUnitPrice,
  viewedTemplateProduct,
} from "../../../../utils/viewedItems";

const useStyles = makeStyles((theme) => {
  return {
    priceContainer: {
      textAlign: "right",
    },
    productContainer: {
      position: "relative",
    },
    stickyHeader: {
      position: "sticky",
      top: clientIsDesktop() ? 0 : "70px",
      zIndex: 1,
      backgroundColor: "rgba(255, 255, 255, 1)",
      boxShadow: "0px 3px 6px #00000029",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      "& .MuiContainer-root": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      },
      "& h3": {
        fontSize: "15px",
        marginTop: "1em",
        marginBottom: "1em",
        [theme.breakpoints.down("lg")]: {
          minWidth: "40vw",
        },
      },
      "& div": {
        minWidth: "20vw",
      },
      "& .original-price": {
        color: "#00000094",
        textDecoration: "line-through",
      },
    },
    prices: {
      textDecoration: "underline",
      cursor: "pointer",
    },
  };
});

/** The templates grouped by the product they print on, in list order. */
function groupTemplatesByProduct(templates, type, getProductByUuid) {
  if (!templates) {
    return [];
  }
  const obj = templates.reduce((products, template) => {
    const product = products[template.product.uuid] || {
      name: template.product.name,
      uuid: template.product.uuid,
      sortOrder: getProductByUuid(template.product.uuid).sortOrder,
      templates: [],
    };
    product.templates.push(template);
    products[product.uuid] = product;
    return products;
  }, {});

  return Object.keys(obj)
    .map((k) => obj[k])
    .sort((a, b) => {
      if (type == "calendars") {
        console.log("Compare function", a, b);
        if (a.name.includes("20x30") && !b.name.includes("20x30")) {
          return -1;
        } else if (!a.name.includes("20x30") && b.name.includes("20x30")) {
          return 1;
        }

        // Pri on 12 pages?
        if (a.name.includes("(12") && !b.name.includes("(12)")) {
          return -1;
        } else if (!a.name.includes("(12") && b.name.includes("(12")) {
          return 1;
        }
      } else if (type == "cards") {
        if (a.name.includes("15x15") && !b.name.includes("15x15")) {
          return -1;
        } else if (!a.name.includes("15x15") && b.name.includes("15x15")) {
          return 1;
        }

        // Sort by size desc:
        let are, bre;
        if (
          (are = a.name.match(/(\d+)x(\d+)/)) &&
          (bre = b.name.match(/(\d)+x(\d+)/))
        ) {
          const aSize = parseInt(are[1]) * parseInt(are[2]);
          const bSize = parseInt(bre[1]) * parseInt(bre[2]);
          if (aSize != bSize) {
            return bSize - aSize;
          }
        }
      }

      if (a.sortOrder === b.sortOrder) {
        return a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      } else {
        return a.sortOrder - b.sortOrder;
      }
    });
}

const ListTemplatesPage = ({
  loading,
  type,
  category,
  canViewWithOwnPictures,
  templates,
  // Before filters; defaults to templates when the page has none.
  allTemplates,
  separateFromBackground,
}) => {
  const {
    getProductByUuid,
    getPriceAsString,
    getPromotedPrice,
    productHasCampaign,
  } = usePriceList();
  const classes = useStyles();
  const { currentPictureSet } = usePictureSets();
  const { qualifyForPromotedPrice } = useShoppingCart();
  const routes = useRoutes();
  const { t } = useTranslation();

  console.log("Loading templates of type", type);

  const [showPriceInfo, setShowPriceInfo] = useState(null);

  const products = useMemo(
    () => groupTemplatesByProduct(templates, type, getProductByUuid),
    [templates],
  );
  // Analytics reports the whole list, so a filter toggle is not a new list.
  const listedProducts = useMemo(
    () =>
      allTemplates
        ? groupTemplatesByProduct(allTemplates, type, getProductByUuid)
        : products,
    [allTemplates, products],
  );

  // Each template is an item: the product it prints on is the item and the
  // template its variant, as on the cart line it becomes.
  const viewedItems = useMemo(() => {
    return listedProducts.flatMap((product) => {
      const priceListProduct = getProductByUuid(product.uuid);
      if (!priceListProduct) {
        return [];
      }
      const price = templateProductUnitPrice(priceListProduct.productprices, {
        campaign: productHasCampaign(priceListProduct, category),
        promotedPrice: qualifyForPromotedPrice
          ? getPromotedPrice(priceListProduct)
          : null,
      });
      return product.templates.map((template) =>
        viewedTemplateProduct(product, template, price),
      );
    });
  }, [listedProducts, category, qualifyForPromotedPrice]);

  useViewItemList(
    loading || currentPictureSet === null
      ? null
      : templateList(type, category),
    viewedItems,
  );

  if (loading || currentPictureSet === null) {
    return <TemplateListSkeleton displayTemplateName={type == "photogifts"} />;
  }

  if (products.length == 0) {
    return (
      <Container>
        <p>
          <Trans>
            There are no products available within this category for the
            selected picture set.
          </Trans>
        </p>
        <p
          dangerouslySetInnerHTML={{
            __html: t("Please change picture set or %schange category%s", {
              postProcess: "sprintf",
              sprintf: [
                "<a href='" +
                  generatePath(routes["products.list"].path) +
                  "' class='MuiTypography-root MuiLink-root MuiLink-underlineHover MuiTypography-colorPrimary'>",
                "</a>",
              ],
            }),
          }}
        />
        {/* Rendered to make sure the classes for the link is added to css */}
        <MUILink href=""></MUILink>
      </Container>
    );
  }

  const getPriceString = (product, categories) => {
    const promotedPrice = getPromotedPrice(product);
    if (qualifyForPromotedPrice && promotedPrice) {
      return (
        <>
          <span className="original-price">
            {formatCurrency(product.productprices[0].price)}
          </span>{" "}
          {formatCurrency(promotedPrice)}
        </>
      );
    }
    return getPriceAsString(product, categories);
  };

  return (
    <React.Fragment>
      {products.map((product) => {
        const p = getProductByUuid(product.uuid);
        const priceString = getPriceString(p, [category]);
        return (
          <section key={product.uuid} className={classes.productContainer}>
            <div className={classes.stickyHeader}>
              <Container onClick={() => setShowPriceInfo(p)}>
                <h3>{product.name}</h3>
                <div className={classes.priceContainer}>
                  {priceString}{" "}
                  {p.productprices.length > 1 && (
                    <span>
                      {" "}
                      (
                      <span className={classes.prices}>
                        <Trans>Prices</Trans>)
                      </span>
                    </span>
                  )}
                </div>
              </Container>
            </div>
            <Container>
              <TemplateList
                product={product}
                templates={product.templates}
                displayTemplateName={type == "photogifts"}
                canViewWithOwnPictures={canViewWithOwnPictures}
                pictureSet={currentPictureSet}
                type={type}
                category={category}
                separateFromBackground={separateFromBackground}
              />
            </Container>
          </section>
        );
      })}
      <ProductPriceInfoPopup
        product={showPriceInfo}
        category={category}
        open={showPriceInfo}
        handleClose={() => setShowPriceInfo(false)}
      />
    </React.Fragment>
  );
};

const TemplateListSkeleton = ({ displayTemplateName }) => {
  const classes = useStyles();

  return (
    <div className={classes.productContainer}>
      <div className={classes.stickyHeader}>
        <Container>
          <h3>
            <Skeleton variant="text" />
          </h3>
          <div>
            <Skeleton variant="text" />
          </div>
        </Container>
      </div>
      <Container>
        <TemplateListItemsSkeleton displayTemplateName={displayTemplateName} />
      </Container>
    </div>
  );
};

export default ListTemplatesPage;
